function normalizeName(name) {
  return String(name || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .replace(/\s*-\s*/g, ' - ');
}

function compactName(name) {
  return normalizeName(name).replace(/\s*-\s*/g, ' ').replace(/\s+/g, ' ');
}

function namesMatch(a, b) {
  if (!a || !b) return false;
  if (a === b) return true;
  return compactName(a) === compactName(b);
}

function getClientFrontend(companySettings) {
  if (!companySettings || Array.isArray(companySettings)) return {};
  return companySettings?.settings?.client_frontend ?? {};
}

export function getFarmPk(farm) {
  const pk = farm?.farm?.pk ?? farm?.farm ?? farm?.pk;
  const n = Number(pk);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function parsePkAllowlist(list) {
  if (Array.isArray(list)) {
    return new Set(
      list
        .map((value) => Number(value))
        .filter((pk) => Number.isFinite(pk) && pk > 0)
    );
  }
  if (typeof list === 'number' && Number.isFinite(list) && list > 0) {
    return new Set([list]);
  }
  return new Set();
}

function getSiloHomeAllowlists(companySettings) {
  const clientFrontend = getClientFrontend(companySettings);
  const farmPks = parsePkAllowlist(clientFrontend.silo_home_farm_pks);
  const barnPks = parsePkAllowlist(clientFrontend.silo_home_barn_pks);
  const farmNamesList = clientFrontend.silo_home_farm_names;
  const farmNames = Array.isArray(farmNamesList)
    ? farmNamesList.map(normalizeName).filter(Boolean)
    : null;

  return {
    farmPks,
    barnPks,
    farmNames: farmNames?.length ? farmNames : null,
    hasRestriction: farmPks.size > 0 || barnPks.size > 0 || farmNames != null,
  };
}

function isSiloHomeFarmFullyEnabled(companySettings, farm) {
  const { farmPks, farmNames } = getSiloHomeAllowlists(companySettings);
  const farmPk = getFarmPk(farm);

  if (farmPk && farmPks.has(farmPk)) return true;
  if (farmNames) {
    const farmName = normalizeName(farm?.name);
    return farmNames.some((allowed) => namesMatch(farmName, allowed));
  }
  return false;
}

/**
 * Granja com visualização graneleira na home.
 * Allowlist: silo_home_farm_pks, silo_home_barn_pks ou silo_home_farm_names.
 */
export function isSiloHomeEnabledForFarm(companySettings, farm, barns = []) {
  const clientFrontend = getClientFrontend(companySettings);
  if (!(clientFrontend.show_silo_home ?? false)) return false;

  const { farmPks, barnPks, farmNames, hasRestriction } = getSiloHomeAllowlists(companySettings);
  if (!hasRestriction) return true;

  const farmPk = getFarmPk(farm);
  if (farmPk && farmPks.has(farmPk)) return true;

  if (farmNames) {
    const farmName = normalizeName(farm?.name);
    if (farmNames.some((allowed) => namesMatch(farmName, allowed))) return true;
  }

  if (barnPks.size > 0 && Array.isArray(barns)) {
    return barns.some((barn) => barnPks.has(Number(barn?.pk)));
  }

  return false;
}

/** Barns visíveis quando a allowlist é por silo_home_barn_pks. */
export function filterSiloHomeBarns(companySettings, farm, barns = []) {
  if (!Array.isArray(barns) || barns.length === 0) return [];

  if (isSiloHomeFarmFullyEnabled(companySettings, farm)) {
    return barns;
  }

  const { barnPks, hasRestriction } = getSiloHomeAllowlists(companySettings);
  if (!hasRestriction || barnPks.size === 0) return barns;

  return barns.filter((barn) => barnPks.has(Number(barn?.pk)));
}
